import React from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Search, Loader2, ChevronLeft, ChevronRight, ChevronDown, Trash, Pencil, Eye, Tag, EyeOff } from "lucide-react";
import { formatCurrencyBRL } from "../../../lib/utils";
import CRMMappingsDialog from "../CRMMappingsDialog";
import { RowActionsMenu } from "./RowActionsMenu";
import CedenteDocumentosPanel from "./CedenteDocumentosPanel";
import { ExternalQueryPanel } from "./ExternalQueryPanel";

const PORTAL_URL = process.env.REACT_APP_PORTAL_URL || window.location.origin;

function openAsCedente(cedenteId, tipo) {
    const params = new URLSearchParams({ id: cedenteId });
    if (tipo) params.set("tipo", tipo);
    window.open(`${PORTAL_URL}/login?${params.toString()}`, "_blank");
}

export function CedentesList({
    cedentes, loading, totalItems, page, setPage, pageSize, setPageSize,
    onUnlink, onBulkUnlink, onBulkInativar, onEdit, onEditDados, onSearch, searchQuery, isDebouncing,
    temValores = true, manualIds = new Set(), onRegisterManual, onToggleAtivo, onToggleFalecido, onTogglePendenteDocumentacao, onToggleRecalculo, tipoAceitePadrao = null,
    onViewAsCedente = null,
    filterTipoAceite = null, onFilterTipoAceite, filterStatus = null, onFilterStatus,
    filterTranche = null, onFilterTranche, availableTranches = [], onAssignTranche,
    onSyncEnvelope = null, syncingEnvelopeId = null,
    onRefresh = () => {}
}) {
  const totalPages = Math.ceil(totalItems / pageSize);
  const [selectedIds, setSelectedIds] = React.useState([]);
  const [isTranchePanelOpen, setIsTranchePanelOpen] = React.useState(false);
  const [trancheInput, setTrancheInput] = React.useState("");
  const [crmDialog, setCrmDialog] = React.useState(null);
  const [panelReloadKey, setPanelReloadKey] = React.useState(0);
  const tranchePanelRef = React.useRef(null);
  const [expandedCedenteId, setExpandedCedenteId] = React.useState(null);
  const [externalQueryPanel, setExternalQueryPanel] = React.useState(null);
  // { cedenteId, processoCedenteId, nome } | null

  React.useEffect(() => {
     setSelectedIds([]);
  }, [cedentes]);

  React.useEffect(() => {
    if (isTranchePanelOpen) {
      setTrancheInput(`Tranche ${availableTranches.length + 1}`);
    }
  }, [isTranchePanelOpen, availableTranches.length]);

  React.useEffect(() => {
    if (!isTranchePanelOpen) return;
    const handler = (e) => {
      if (tranchePanelRef.current && !tranchePanelRef.current.contains(e.target)) {
        setIsTranchePanelOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isTranchePanelOpen]);

  const handleSelectAll = (e) => {
      if (e.target.checked) {
          setSelectedIds(cedentes.map(pc => pc.cedente_id));
      } else {
          setSelectedIds([]);
      }
  };

  const handleSelectOne = (e, cedenteId) => {
      if (e.target.checked) {
          setSelectedIds(prev => [...prev, cedenteId]);
      } else {
          setSelectedIds(prev => prev.filter(id => id !== cedenteId));
      }
  };

  const handleBulkDelete = () => {
      if (selectedIds.length === 0) return;
      onBulkUnlink(selectedIds);
  };

  const handleBulkInativar = () => {
      if (selectedIds.length === 0) return;
      onBulkInativar(selectedIds);
  };

  const isAllSelected = cedentes.length > 0 && selectedIds.length === cedentes.length;

  return (
    <>
      <div className="mb-4">
        <div className="relative flex justify-between items-center">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
                placeholder="Buscar substituídos vinculados..."
                className="pl-8 w-full"
                value={searchQuery}
                onChange={(e) => onSearch(e.target.value)}
            />
            {isDebouncing && (
                <div className="absolute right-3 top-2.5 flex items-center text-xs text-muted-foreground gap-1 animate-pulse">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Buscando...
                </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {selectedIds.length > 0 && (
                <Button variant="outline" className="border-statusWarning/30 text-amber-600 hover:bg-statusWarningBg" onClick={handleBulkInativar}>
                    <EyeOff className="h-4 w-4 mr-2" />
                    Inativar ({selectedIds.length})
                </Button>
            )}
            {selectedIds.length > 0 && (
                <Button variant="destructive" onClick={handleBulkDelete}>
                    <Trash className="h-4 w-4 mr-2" />
                    Excluir ({selectedIds.length})
                </Button>
            )}

            {selectedIds.length > 0 && (
              <div className="relative" ref={tranchePanelRef}>
                <Button
                  variant="outline"
                  className="border-indigo-300 text-indigo-600 hover:bg-indigo-50"
                  onClick={() => setIsTranchePanelOpen(v => !v)}
                >
                  <Tag className="h-4 w-4 mr-2" />
                  Gerar Tranche ({selectedIds.length})
                </Button>

                {isTranchePanelOpen && (
                  <div className="absolute right-0 top-10 z-50 bg-card border border-border rounded-lg shadow-lg p-3 w-64 space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Nome da tranche</p>
                    <Input
                      value={trancheInput}
                      onChange={e => setTrancheInput(e.target.value)}
                      className="h-8 text-sm"
                      placeholder="ex: Tranche 1"
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                        disabled={!trancheInput.trim()}
                        onClick={() => {
                          onAssignTranche(selectedIds, trancheInput.trim());
                          setIsTranchePanelOpen(false);
                        }}
                      >
                        Atribuir
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:bg-statusErrorBg"
                        onClick={() => {
                          onAssignTranche(selectedIds, null);
                          setIsTranchePanelOpen(false);
                        }}
                      >
                        Remover
                      </Button>
                    </div>
                    <button
                      className="absolute top-2 right-2 text-muted-foreground hover:text-muted-foreground text-xs"
                      onClick={() => setIsTranchePanelOpen(false)}
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-2">
          <select
            className="h-8 rounded-md border border-input bg-background px-3 py-1 text-sm"
            value={filterTipoAceite ?? ""}
            onChange={e => onFilterTipoAceite(e.target.value || null)}
          >
            <option value="">Todos os aceites</option>
            <option value="acordo">Acordo</option>
            <option value="cessao">Cessão</option>
          </select>

          <select
            className="h-8 rounded-md border border-input bg-background px-3 py-1 text-sm"
            value={filterStatus ?? ""}
            onChange={e => onFilterStatus(e.target.value || null)}
          >
            <option value="">Todos os status</option>
            <option value="pendente">Pendente</option>
            <option value="analise">Em Análise</option>
            <option value="assinado">Assinado</option>
            <option value="concluido">Concluído</option>
            <option value="pago">Pago</option>
            <option value="expirado">Expirado</option>
          </select>

          <select
            className="h-8 rounded-md border border-input bg-background px-3 py-1 text-sm"
            value={filterTranche ?? ""}
            onChange={e => onFilterTranche(e.target.value || null)}
          >
            <option value="">Todas as tranches</option>
            <option value="__none__">Sem tranche</option>
            {availableTranches.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>

          {(filterTipoAceite || filterStatus || filterTranche) && (
            <button
              className="h-8 px-3 text-sm text-muted-foreground hover:text-foreground underline"
              onClick={() => { onFilterTipoAceite(null); onFilterStatus(null); onFilterTranche(null); }}
            >
              Limpar filtros
            </button>
          )}
        </div>
      </div>
      
      {loading ? (
          <div className="flex justify-center items-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
      ) : (
        <>
        <div className="rounded-md border">
            <Table>
                <TableHeader>
                <TableRow>
                    <TableHead className="w-[50px]">
                        <input 
                            type="checkbox" 
                            className="rounded border-gray-300"
                            checked={isAllSelected}
                            onChange={handleSelectAll}
                        />
                    </TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>CPF</TableHead>
                    {tipoAceitePadrao && <TableHead>Aceite</TableHead>}
                    <TableHead>Status</TableHead>
                    {temValores && <TableHead>Valor Prop.</TableHead>}
                    {temValores && <TableHead>Valor Crédito</TableHead>}
                    <TableHead className="w-[120px] text-right">Ações</TableHead>
                </TableRow>
                </TableHeader>
                <TableBody>
                {cedentes.map(pc => (
                    <React.Fragment key={pc.id}>
                    <TableRow>
                    <TableCell>
                        <input 
                            type="checkbox" 
                            className="rounded border-gray-300"
                            checked={selectedIds.includes(pc.cedente_id)}
                            onChange={(e) => handleSelectOne(e, pc.cedente_id)}
                        />
                    </TableCell>
                    <TableCell>
                        <button
                            className="flex items-center gap-1 text-left font-medium text-foreground cursor-pointer hover:text-primary active:scale-[0.98] transition-colors duration-150 group"
                            onClick={() => setExpandedCedenteId((prev) => (prev === pc.cedente_id ? null : pc.cedente_id))}
                            title={expandedCedenteId === pc.cedente_id ? "Fechar documentos" : "Ver documentos"}
                        >
                            <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-muted-foreground group-hover:text-blue-400 transition-transform duration-200 ${expandedCedenteId === pc.cedente_id ? "rotate-180" : ""}`} />
                            {pc.cedente?.nome}
                        </button>

                        {pc.cedente?.ativo === false && (
                            <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-statusErrorBg text-statusError ml-1">Inativo</span>
                        )}
                        {pc.cedente?.falecido && (
                            <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-gray-800 text-gray-100 ml-1">Falecido</span>
                        )}
                        {pc.cedente?.pendente_documentacao && (
                            <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-statusInfoBg text-statusInfo border border-statusInfo/30 ml-1">Pend. Doc.</span>
                        )}
                        {pc.requer_recalculo && (
                            <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-statusWarningBg text-statusWarning border border-statusWarning/30 ml-1">Em recálculo</span>
                        )}
                        {manualIds.has(pc.cedente_id) && (
                            <span className="ml-2 px-1.5 py-0.5 rounded text-xs font-semibold bg-statusWarningBg text-statusWarning border border-statusWarning/30" title="Possui assinatura(s) manual(is) registrada(s)">
                                ✍ MANUAL
                            </span>
                        )}
                        {pc.tranche && (
                            <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 ml-1">
                                {pc.tranche}
                            </span>
                        )}
                    </TableCell>
                    <TableCell className="font-mono tabular-nums text-xs">{pc.cedente?.cpf}</TableCell>
                    {tipoAceitePadrao && (
                        <TableCell>
                            {pc.tipo_aceite === "acordo" && <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-statusInfoBg text-statusInfo">Acordo</span>}
                            {pc.tipo_aceite === "cessao" && <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-statusSuccessBg text-statusSuccess">Cessão</span>}
                            {!pc.tipo_aceite && <span className="text-xs text-muted-foreground">—</span>}
                        </TableCell>
                    )}
                    <TableCell>
                        {pc.status === "concluido"
                            ? <span className="px-2 py-1 rounded-full text-xs bg-statusSuccessBg text-statusSuccess">Concluído</span>
                            : pc.status === "assinado"
                            ? <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">Assinado</span>
                            : pc.status === "analise"
                            ? <span className="px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-700">Em Análise</span>
                            : pc.status === "pago"
                            ? <span className="px-2 py-1 rounded-full text-xs bg-statusInfoBg text-statusInfo">Pago</span>
                            : pc.status === "expirado"
                            ? <span className="px-2 py-1 rounded-full text-xs bg-statusErrorBg text-statusError">Expirado</span>
                            : <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">Pendente</span>
                        }
                    </TableCell>
                    {temValores && (
                        <TableCell>
                            <span className="font-mono tabular-nums">{pc.valor_proposta_final ? formatCurrencyBRL(pc.valor_proposta_final) : '-'}</span>
                        </TableCell>
                    )}
                    {temValores && (
                        <TableCell>
                            <span className="font-mono tabular-nums">{(pc.valor_parcelado ?? pc.valor_credito) ? `R$ ${(pc.valor_parcelado ?? pc.valor_credito).toLocaleString('pt-BR')}` : '-'}</span>
                        </TableCell>
                    )}
                    <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-0.5">
                            <Button
                                variant="ghost"
                                size="sm"
                                title="Editar proposta"
                                onClick={() => onEdit(pc)}
                            >
                                <Pencil className="h-4 w-4 text-blue-500" />
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                title="Ver como cedente"
                                onClick={() => onViewAsCedente ? onViewAsCedente(pc.cedente_id) : openAsCedente(pc.cedente_id, tipoAceitePadrao)}
                            >
                                <Eye className="h-4 w-4 text-muted-foreground" />
                            </Button>
                            <RowActionsMenu
                                ativo={pc.cedente?.ativo}
                                falecido={pc.cedente?.falecido}
                                pendenteDocumentacao={pc.cedente?.pendente_documentacao}
                                requerRecalculo={pc.requer_recalculo}
                                showEditDados={!!onEditDados}
                                showRegisterManual={!!onRegisterManual}
                                showToggleAtivo={!!onToggleAtivo}
                                showToggleFalecido={!!onToggleFalecido}
                                showTogglePendenteDocumentacao={!!onTogglePendenteDocumentacao}
                                showToggleRecalculo={!!onToggleRecalculo}
                                onEditDados={() => onEditDados(pc.cedente)}
                                onRegisterManual={() => onRegisterManual(pc.cedente)}
                                onToggleAtivo={() => {
                                    if (pc.cedente?.ativo === false) {
                                        onToggleAtivo(pc.cedente_id);
                                    } else if (window.confirm("Isso bloqueará o acesso do cedente à plataforma. Confirmar?")) {
                                        onToggleAtivo(pc.cedente_id);
                                    }
                                }}
                                onToggleFalecido={() => onToggleFalecido(pc.cedente_id, pc.cedente?.falecido, pc.cedente)}
                                onTogglePendenteDocumentacao={() => onTogglePendenteDocumentacao(pc.cedente_id, pc.cedente?.pendente_documentacao)}
                                onToggleRecalculo={() => onToggleRecalculo(pc.cedente_id, pc.requer_recalculo)}
                                onOpenCrm={() => { 
                                    setExpandedCedenteId(null); 
                                    setCrmDialog({ 
                                        id: pc.id, 
                                        cedenteId: pc.cedente_id, 
                                        name: pc.cedente?.nome,
                                        processoId: pc.processo_id,
                                        status: pc.status
                                    }); 
                                }}
                                onUnlink={() => onUnlink(pc.cedente_id)}
                                onExternalQuery={() => setExternalQueryPanel({ cedenteId: pc.cedente_id, processoCedenteId: pc.id, nome: pc.cedente?.nome })}
                                onSyncEnvelope={onSyncEnvelope ? () => onSyncEnvelope(pc.cedente_id, pc.processo_id) : null}
                                syncingEnvelope={syncingEnvelopeId === pc.cedente_id}
                            />
                        </div>
                    </TableCell>
                    </TableRow>
                    {expandedCedenteId === pc.cedente_id && (
                        <TableRow>
                            <TableCell
                                colSpan={5 + (tipoAceitePadrao ? 1 : 0) + (temValores ? 2 : 0)}
                                className="p-0 bg-muted"
                            >
                                <CedenteDocumentosPanel
                                    key={panelReloadKey}
                                    cedenteId={pc.cedente_id}
                                    processoId={pc.processo_id}
                                    processoCedenteId={pc.id}
                                    status={pc.status}
                                    onDocumentosChanged={onRefresh}
                                />
                            </TableCell>
                        </TableRow>
                    )}
                    </React.Fragment>
                ))}
                {cedentes.length === 0 && (
                    <TableRow>
                    <TableCell colSpan={temValores ? 7 : 5} className="text-center text-muted-foreground">Nenhum substituído vinculado</TableCell>
                    </TableRow>
                )}
                </TableBody>
            </Table>
        </div>
        
        <div className="flex items-center justify-between space-x-2 py-4">
            <div className="flex-1 text-sm text-muted-foreground flex items-center space-x-2">
                <span>Total: {totalItems}</span>
                {setPageSize && (
                    <select
                        className="ml-4 h-8 w-20 rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background disabled:cursor-not-allowed disabled:opacity-50"
                        value={pageSize}
                        onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            setPage(1); // Reset to first page
                        }}
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={totalItems || 1000}>Tudo</option>
                    </select>
                )}
            </div>
            <div className="space-x-2">
                <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1 || loading}
                >
                <ChevronLeft className="h-4 w-4" /> Anterior
                </Button>
                <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages || totalPages === 0 || loading}
                >
                Próximo <ChevronRight className="h-4 w-4" />
                </Button>
            </div>
        </div>
        </>
      )}
      {crmDialog && (
          <CRMMappingsDialog
              entityType="processo_cedente"
              entityId={crmDialog.id}
              cedenteId={crmDialog.cedenteId}
              processoId={crmDialog.processoId}
              status={crmDialog.status}
              title={`Substituído: ${crmDialog.name}`}
              onClose={() => setCrmDialog(null)}
              onMappingDeleted={() => setPanelReloadKey(k => k + 1)}
              onStatusChanged={onRefresh}
          />
      )}
      {externalQueryPanel && (
        <ExternalQueryPanel
          cedenteId={externalQueryPanel.cedenteId}
          processoCedenteId={externalQueryPanel.processoCedenteId}
          cedenteNome={externalQueryPanel.nome}
          onClose={() => setExternalQueryPanel(null)}
        />
      )}
    </>
  );
}
